/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * ItemLineageTimeline
 *
 * Thin wrapper: derives item-specific lineage events from store data and
 * delegates rendering to the shared LineageTimeline renderer.
 */

import { useMemo, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Map as MapIcon } from 'lucide-react'
import { useItemsStore } from '../../stores/prioritization/itemsStore'
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore'
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore'
import { useSessionStore } from '../../stores/prioritization/sessionStore'
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore'
import { useCycleParticipationStore } from '../../stores/prioritization/cycleParticipationStore'
import { useAuditStore, type AuditEntry } from '../../stores/prioritization/auditStore'
import { useRescoringStore } from '../../stores/prioritization/rescoringStore'
import { rescoringAttributeDef, TRIGGER_TYPE_DEFS } from '../../types/prioritization/rescoringEvents'
import { useAttributesStore } from '../../stores/prioritization/attributesStore'
import { getLineageForItem, type LineageEventRow } from '../../lib/lineageService'
import { auditToItemEvent, fieldChangeTitle, isCycleActivity, sessionIncludesItem, type ItemEventKind } from './activity/itemEventMapping'
import { buildDescription, changeLines, changeValueText, namedPeopleIds } from './item-view/activity/historyWords'
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap'
import { useItemSaveTick } from './item-view/activity/HistoryTab'
import { relationshipGroupTimelineEntry } from '../../lib/relationshipGroupTimeline'
import {
  LineageTimeline,
  type TimelineEvent,
  type LineageEventType,
  type EventStream,
  toTimelineDate,
} from './activity/LineageTimeline'

// EventStream is used internally to tag events for potential future filtering.

// ─── Stream classification ────────────────────────────────────────────────────

/** Event types that are structural identity transforms (Lineage stream). */
const LINEAGE_TYPES = new Set<LineageEventType>([
  'lineage_split',
  'lineage_merge',
  'lineage_supersede',
  'lineage_fork',
  'lineage_retire',
])

/**
 * Turn an event-type slug into a readable label.
 *
 * The old version split on '_' only, so a camelCase type
 * ('catchballBallAutoAssigned') survived as one word and — once the row's
 * uppercase styling hit it — rendered as CATCHBALLBALLAUTOASSIGNED. Splitting
 * on the case boundary as well as the underscore handles both spellings, and
 * '-' too, since event types arrive in all three shapes.
 */
function humanizeEventType(type: string): string {
  return type
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function streamFor(type: LineageEventType): EventStream {
  return LINEAGE_TYPES.has(type) ? 'lineage' : 'history'
}

// ─── Audit → lifecycle event mapping ─────────────────────────────────────────
//
// The mapping itself lives in activity/itemEventMapping.ts, shared with the
// Item Journey page. This only translates its neutral vocabulary into this
// timeline's event union.

const KIND_TO_TIMELINE: Record<ItemEventKind, LineageEventType> = {
  created: 'created',
  imported: 'imported',
  shaping_started: 'shaping_started',
  shaping_completed: 'shaping_completed',
  leadership_approved: 'leadership_approved',
  promoted: 'promoted',
  archived: 'archived',
  deferred: 'deferred',
  reactivated: 'reactivated',
  completed: 'completed',
  retired: 'lineage_retire',
  classification_changed: 'classification_changed',
  parent_changed: 'parent_changed',
  dedupe_reviewed: 'dedupe_reviewed',
  pool_added: 'pool_added',
  cycle_added: 'cycle_added',
  relationship_added: 'relationship_added',
  relationship_dissolved: 'relationship_dissolved',
  decision_recorded: 'decision_recorded',
  lineage_split: 'lineage_split',
  lineage_fork: 'lineage_fork',
  item_changed: 'item_changed',
}

function auditToTimelineType(et: string, toState?: string, fromState?: string): LineageEventType | null {
  const kind = auditToItemEvent(et, toState, fromState)
  return kind ? KIND_TO_TIMELINE[kind] : null
}

// ─── Event derivation ────────────────────────────────────────────────────────

type CycleParticipation = ReturnType<typeof useCycleParticipationStore>['participations'][string]

function deriveEvents(
  item: ReturnType<typeof useItemsStore>['items'][number],
  sessions: ReturnType<typeof useSessionStore>['sessions'],
  cycles: ReturnType<typeof useDegapProcessStore>['cycles'],
  groups: ReturnType<typeof useItemRelationshipsStore>['groups'],
  edges: ReturnType<typeof useItemRelationshipsStore>['edges'],
  allItems: ReturnType<typeof useItemsStore>['items'],
  cycleParticipations: CycleParticipation[],
  auditEntries?: AuditEntry[],
  lineageRows?: LineageEventRow[],
  /** Cycle-bound item pools — how items actually reach a cycle today. */
  pools?: { id: string; name?: string; cycleId?: string; priorityIds?: string[]; createdAt?: Date | string }[],
  /** User id → name, for a change that names a person (owner, delegate). */
  names?: Record<string, string>,
): TimelineEvent[] {
  const events: TimelineEvent[] = []

  const push = (e: Omit<TimelineEvent, 'stream'> & { type: LineageEventType }) => {
    events.push({ ...e, stream: streamFor(e.type) })
  }

  // ── Lifecycle events: audit rows first, then anything the item's own fields
  // know that the audit trail didn't record.
  //
  // These used to be either/or: any audit row at all disabled the field
  // derivation entirely, and the audit branch then dropped every type outside a
  // short allowlist. An item with history showed less than an item without.
  const typesFromAudit = new Set<LineageEventType>()
  // The History log's own wording, so a change reads the same in both places.
  const sessionNames: Record<string, string> = Object.fromEntries(sessions.map((s) => [s.id, s.name ?? 'Session']))
  const itemById = (id: string) => allItems.find((i) => i.id === id) ?? null
  if (auditEntries && auditEntries.length > 0) {
    for (const entry of auditEntries) {
      // The cycle's own record (cut line moved, ball claimed) is not this item's lineage.
      if (isCycleActivity(entry)) continue
      const ts = toTimelineDate(entry.timestamp)
      if (!ts) continue
      const type = auditToTimelineType(entry.eventType, entry.toState, entry.fromState)
      if (!type) continue
      // A field edit is titled by what changed, not by its kind ("Classification changed").
      const change = fieldChangeTitle(entry.changeSummary)
      typesFromAudit.add(type)
      push({
        id: `audit-${entry.id}`,
        type,
        timestamp: ts,
        // The History log's title for the same record, so the two read alike.
        label: buildDescription(entry, sessionNames, itemById, item.itemCategory) || change?.label || humanizeEventType(type),
        actor: entry.userId,
        detail: type === 'item_changed'
          ? changeLines(entry, itemById, names).map(changeValueText).join(' · ') || undefined
          : entry.note ?? change?.detail ?? (entry.actorName ? `By ${entry.actorName}` : undefined),
      })
    }
  }
  {
    // Field derivation — skipped per type when the audit trail already has it,
    // so a recorded event is never duplicated by an inferred one.
    const pushIfNew = (e: Omit<TimelineEvent, 'stream'> & { type: LineageEventType }) => {
      if (typesFromAudit.has(e.type)) return
      push(e)
    }
    const createdAt = toTimelineDate(item.createdAt)
    if (createdAt) {
      pushIfNew({ id: 'created', type: 'created', timestamp: createdAt, label: 'Item Created',
        actor: item.createdBy as string | undefined })
    }
    const shapingStartedAt = toTimelineDate((item as any).shapingStartedAt)
    if (shapingStartedAt) pushIfNew({ id: 'shaping-started', type: 'shaping_started', timestamp: shapingStartedAt, label: 'Work Started', actor: (item as any).shapingOwnerId })
    const shapedAt = toTimelineDate((item as any).shapedAt)
    if (shapedAt) pushIfNew({ id: 'shaping-completed', type: 'shaping_completed', timestamp: shapedAt, label: 'Item Ready' })
    const leadershipApprovedAt = toTimelineDate((item as any).leadershipApprovedAt)
    if (leadershipApprovedAt) pushIfNew({ id: 'leadership-approved', type: 'leadership_approved', timestamp: leadershipApprovedAt, label: 'Leadership Approved', actor: (item as any).leadershipApprovedBy })
    const promotedAt = toTimelineDate((item as any).promotedAt)
    if (promotedAt) pushIfNew({ id: 'promoted', type: 'promoted', timestamp: promotedAt, label: 'Promoted to Priority', actor: (item as any).promotedBy })
    const exitStatus = (item as any).exit_status as string | undefined
    const updatedAt = toTimelineDate(item.updatedAt)
    const createdAtFallback = toTimelineDate(item.createdAt)
    if (exitStatus === 'deferred' && updatedAt && updatedAt !== createdAtFallback)
      pushIfNew({ id: 'deferred', type: 'deferred', timestamp: updatedAt, label: 'Deferred', isEstimated: true })
    if (exitStatus === 'archived' && updatedAt && updatedAt !== createdAtFallback)
      pushIfNew({ id: 'archived', type: 'archived', timestamp: updatedAt, label: 'Archived', isEstimated: true })
  }

  // ── Identity events: lineage_events DB is authoritative ─────────────────────
  const createdAt = toTimelineDate(item.createdAt)
  if (lineageRows && lineageRows.length > 0) {
    for (const row of lineageRows) {
      const ts = toTimelineDate(row.created_at)
      if (!ts) continue
      const isSource = row.source_item_ids.includes(item.id)
      const isTarget = row.target_item_ids.includes(item.id)

      if (row.event_type === 'merge' && isTarget) {
        const sourceNames = row.source_item_ids.map((id) => allItems.find((i: any) => i.id === id)?.title ?? id.slice(0, 8))
        push({ id: `lineage-${row.id}`, type: 'lineage_merge', timestamp: ts,
          label: `Merged (${row.source_item_ids.length} items)`, detail: sourceNames.join(', '),
          actor: row.performed_by ?? undefined,
          linkedId: row.source_item_ids[0],
          linkedLabel: allItems.find((i: any) => i.id === row.source_item_ids[0])?.title ?? 'View source',
          linkedPath: row.source_item_ids[0] ? `/prioritization/items/${row.source_item_ids[0]}` : undefined })
      } else if (row.event_type === 'split' && isTarget) {
        const parentId = row.source_item_ids[0]
        const parent = allItems.find((i: any) => i.id === parentId)
        push({ id: `lineage-${row.id}`, type: 'lineage_split', timestamp: ts,
          label: 'Split from Item', detail: parent?.title ?? row.rationale ?? undefined,
          actor: row.performed_by ?? undefined,
          linkedId: parentId, linkedLabel: parent?.title ?? 'View parent',
          linkedPath: parentId ? `/prioritization/items/${parentId}` : undefined })
      } else if ((row.event_type === 'fork') && isTarget) {
        const parentId = row.source_item_ids[0]
        const parent = allItems.find((i: any) => i.id === parentId)
        push({ id: `lineage-${row.id}`, type: 'lineage_fork', timestamp: ts,
          label: 'Forked from Item', detail: parent?.title ?? undefined,
          actor: row.performed_by ?? undefined,
          linkedId: parentId, linkedLabel: parent?.title ?? 'View parent',
          linkedPath: parentId ? `/prioritization/items/${parentId}` : undefined })
      } else if (row.event_type === 'supersede' && isSource) {
        const successor = row.target_item_ids[0]
        const successorItem = allItems.find((i: any) => i.id === successor)
        push({ id: `lineage-${row.id}`, type: 'lineage_supersede', timestamp: ts,
          label: 'Superseded', detail: successorItem?.title ?? row.rationale ?? undefined,
          actor: row.performed_by ?? undefined,
          linkedId: successor, linkedLabel: successorItem?.title ?? 'View successor',
          linkedPath: successor ? `/prioritization/items/${successor}` : undefined })
      } else if (row.event_type === 'retire' && isSource) {
        push({ id: `lineage-${row.id}`, type: 'lineage_retire', timestamp: ts,
          label: 'Retired', detail: row.rationale ?? undefined, actor: row.performed_by ?? undefined })
      } else if (row.event_type === 'split' && isSource) {
        push({ id: `lineage-${row.id}-src`, type: 'lineage_split', timestamp: ts,
          label: `Split into ${row.target_item_ids.length} items`,
          detail: row.rationale ?? undefined, actor: row.performed_by ?? undefined })
      }
    }
  } else {
    // Fallback: item field derivation for split/merge when no lineage rows.
    const splitFromId = (item as any).splitFromId
    if (splitFromId) {
      const parent = allItems.find((i: any) => i.id === splitFromId)
      push({ id: 'lineage-split', type: 'lineage_split', timestamp: createdAt ?? new Date(),
        label: 'Split from Item', detail: parent?.title ?? splitFromId,
        linkedId: splitFromId, linkedLabel: parent?.title ?? 'View parent',
        linkedPath: `/prioritization/items/${splitFromId}`, isEstimated: !createdAt })
    }
    const mergedFromIds = (item as any).mergedFromIds as string[] | undefined
    if (mergedFromIds?.length) {
      const names = mergedFromIds.map((id: string) => allItems.find((i: any) => i.id === id)?.title ?? id.slice(0, 8)).join(', ')
      push({ id: 'lineage-merge', type: 'lineage_merge', timestamp: createdAt ?? new Date(),
        label: `Merged (${mergedFromIds.length} items)`, detail: names, isEstimated: !createdAt })
    }
  }

  // 9. Cycle participation (from participation store — rich provenance)
  cycleParticipations.forEach((p) => {
    const addedAt = toTimelineDate(p.addedAt)
    if (!addedAt) return

    const cycleName = cycles.find((c: any) => c.id === p.cycleId)?.name

    // Entry event
    push({
      id: `cycle-participation-${p.id}`,
      type: 'cycle_added',
      timestamp: addedAt,
      label: cycleName ?? 'Prioritization Cycle',
      detail: p.inclusionNote
        ? p.inclusionNote
        : p.inclusionReason === 'carryover'
          ? 'Carried over from previous cycle'
          : p.inclusionReason === 'stakeholder_request'
            ? 'Added via stakeholder request'
            : p.inclusionReason === 'criteria_match'
              ? 'Added by criteria match'
              : undefined,
      actor: p.addedBy !== 'system' ? p.addedBy : undefined,
      linkedId: p.cycleId,
      linkedLabel: cycleName ?? 'View cycle',
      linkedPath: `/prioritization/cycles/${p.cycleId}`,
    })

    // Carryover gets its own event
    if (p.inclusionReason === 'carryover') {
      push({
        id: `cycle-carryover-${p.id}`,
        type: 'cycle_carryover',
        timestamp: addedAt,
        label: 'Carried Over',
        detail: cycleName ? `Into: ${cycleName}` : undefined,
        actor: p.addedBy !== 'system' ? p.addedBy : undefined,
        linkedId: p.cycleId,
        linkedLabel: cycleName ?? 'View cycle',
        linkedPath: `/prioritization/cycles/${p.cycleId}`,
      })
    }

    // Exclusion event
    if (p.excludedAt) {
      const excludedAt = toTimelineDate(p.excludedAt)
      if (excludedAt) {
        push({
          id: `cycle-excluded-${p.id}`,
          type: 'cycle_status_changed',
          timestamp: excludedAt,
          label: 'Removed from Cycle',
          detail: (p.excludedReason ?? cycleName) ? `${cycleName ?? ''}${p.excludedReason ? ` · ${p.excludedReason}` : ''}` : undefined,
          actor: p.excludedBy !== 'system' ? (p.excludedBy ?? undefined) : undefined,
          linkedId: p.cycleId,
          linkedLabel: cycleName ?? 'View cycle',
          linkedPath: `/prioritization/cycles/${p.cycleId}`,
        })
      }
    }
  })

  // 10. Legacy cycles (fallback for cycles that don't use participation store)
  const legacyCycleIds = new Set(cycleParticipations.map((p) => p.cycleId))
  const itemCycles = cycles.filter((c: any) => c.itemIds?.includes(item.id) && !legacyCycleIds.has(c.id))
  itemCycles.forEach((cycle: any) => {
    const ts = toTimelineDate(cycle.createdAt)
    if (!ts) return
    push({
      id: `cycle-legacy-${cycle.id}`,
      type: 'cycle_added',
      timestamp: ts,
      label: cycle.name ?? 'Prioritization Cycle',
      detail: cycle.phase ? `Phase: ${cycle.phase}` : undefined,
      linkedId: cycle.id,
      linkedLabel: cycle.name ?? 'View cycle',
      linkedPath: `/prioritization/cycles/${cycle.id}`,
    })
  })

  // 10b. Cycles reached through a pool. `cycle_item_participation` is the
  // canonical record (canon 03 §Cycle Item Participation), but nothing has
  // written it since 2026-07-22: items now enter a cycle through a cycle-bound
  // item pool, which records the comparison set and an audit event but no
  // participation row. Without this the Cycles lane is empty for every item in
  // every cycle created since.
  const shownCycleIds = new Set(events.filter((e) => e.type === 'cycle_added').map((e) => e.linkedId))
  ;(pools ?? []).forEach((pool) => {
    if (!pool.cycleId || !pool.priorityIds?.includes(item.id)) return
    if (shownCycleIds.has(pool.cycleId)) return
    shownCycleIds.add(pool.cycleId)
    const cycle = cycles.find((c: any) => c.id === pool.cycleId)
    const ts = toTimelineDate(pool.createdAt) ?? createdAt
    if (!ts) return
    push({
      id: `cycle-via-pool-${pool.cycleId}-${pool.id}`,
      type: 'cycle_added',
      timestamp: ts,
      label: (cycle as any)?.name ?? 'Cycle',
      sublabel: pool.name ? `via ${pool.name}` : 'via item pool',
      // The pool keeps no per-item timestamp, so this is the pool's own date.
      isEstimated: true,
      linkedId: pool.cycleId,
      linkedLabel: (cycle as any)?.name ?? 'View cycle',
      linkedPath: `/prioritization/cycles/${pool.cycleId}`,
    })
  })

  // 11. Sessions — include rank + score from rankingOutput
  const itemSessions = sessions.filter((s: any) => sessionIncludesItem(s, item.id))
  itemSessions.forEach((session: any) => {
    const ts = toTimelineDate(session.createdAt)
    if (!ts) return

    let rankDetail: string | undefined
    const ro = session.rankingOutput as {
      rankedItemIds?: string[]
      orderedItems?: { id: string }[]
      scores?: Record<string, Record<string, number> & { total?: number }>
    } | undefined
    if (ro) {
      const orderedIds = ro.rankedItemIds ?? ro.orderedItems?.map((i) => i.id) ?? []
      const rankIndex = orderedIds.indexOf(item.id)
      const rank = rankIndex >= 0 ? rankIndex + 1 : undefined
      const score = ro.scores?.[item.id]?.total
      const parts: string[] = []
      if (rank != null) parts.push(`#${rank} of ${orderedIds.length}`)
      if (score != null) parts.push(`score ${score.toFixed(1)}`)
      if (parts.length) rankDetail = parts.join(' · ')
    }

    push({
      id: `session-${session.id}`,
      type: 'session_added',
      timestamp: ts,
      label: session.name ?? 'Ranking Session',
      detail: rankDetail ?? (session.state ? `State: ${session.state}` : undefined),
      linkedId: session.id,
      linkedLabel: session.name ?? 'View session',
      linkedPath: `/prioritization/sessions/${session.id}`,
    })
  })

  // 12. Relationship groups (packages, clusters)
  groups.forEach((group: any) => {
    const ts = toTimelineDate(group.createdAt)
    if (!ts) return
    const entry = relationshipGroupTimelineEntry(group)
    push({
      id: `group-${group.id}`,
      type: entry.eventType,
      timestamp: ts,
      label: entry.label,
      detail: group.rationale,
      actor: group.createdBy ?? undefined,
      linkedId: group.id,
      linkedLabel: entry.linkedLabel,
      linkedPath: entry.linkedPath,
    })
    if (group.status === 'dissolved') {
      push({
        id: `group-dissolved-${group.id}`,
        type: 'relationship_dissolved' as const,
        timestamp: ts,
        label: `${entry.typeName} Dissolved`,
        detail: group.title ?? undefined,
        isEstimated: true,
      })
    }
  })

  // 13. Relationship edges (dependencies, contains, etc.)
  const itemEdges = edges.filter(
    (e: any) => e.sourceItemId === item.id || e.targetItemId === item.id,
  )
  itemEdges.forEach((edge: any) => {
    const ts = toTimelineDate(edge.createdAt)
    if (!ts) return
    const isSource = edge.sourceItemId === item.id
    const otherId = isSource ? edge.targetItemId : edge.sourceItemId
    const other = allItems.find((i: any) => i.id === otherId)
    const edgeRoute =
      edge.type === 'dependency' ? `/prioritization/relationships/dependencies`
      : edge.type === 'prerequisite' ? `/prioritization/relationships/prerequisites`
      : edge.type === 'elaboration' ? `/prioritization/relationships/elaborations`
      : edge.type === 'aggregation' ? `/prioritization/relationships/aggregations`
      : edge.type === 'breakdown' ? `/prioritization/relationships/breakdown`
      : edge.type === 'contains' ? `/prioritization/relationships/contains`
      : undefined
    const edgeEventType: LineageEventType =
      edge.type === 'dependency' ? 'dependency_added'
      : edge.type === 'prerequisite' ? 'prerequisite_added'
      : edge.type === 'aggregation' ? 'aggregation_added'
      : edge.type === 'elaboration' ? 'elaboration_added'
      : edge.type === 'breakdown' ? 'breakdown_added'
      : edge.type === 'contains' ? 'contains_added'
      : 'dependency_added'
    const edgeLabel =
      edge.type === 'dependency' ? (isSource ? '→ Depends On' : '← Required By')
      : edge.type === 'prerequisite' ? (isSource ? '→ Prerequisite For' : '← Requires')
      : edge.type === 'aggregation' ? (isSource ? '→ Aggregates' : '← Aggregated by')
      : edge.type === 'breakdown' ? (isSource ? '→ Breaks Down Into' : '← Part Of')
      : edge.type === 'contains' ? (isSource ? '→ Contains' : '← Contained By')
      : edge.type === 'elaboration' ? (isSource ? '→ Elaborates' : '← Elaborated by')
      : 'Related'

    push({
      id: `edge-${edge.id}`,
      type: edgeEventType,
      timestamp: ts,
      label: edgeLabel,
      sublabel: edge.subtype ?? undefined,
      detail: other?.title ?? otherId?.slice(0, 8),
      actor: edge.createdBy ?? undefined,
      linkedId: otherId,
      linkedLabel: other?.title ?? 'View item',
      linkedPath: other ? `/prioritization/items/${otherId}` : edgeRoute,
    })
    if (edge.status === 'dissolved') {
      const dissolvedLabel =
        edge.type === 'dependency' ? 'Dependency Dissolved'
        : edge.type === 'prerequisite' ? 'Prerequisite Dissolved'
        : edge.type === 'aggregation' ? 'Aggregation Dissolved'
        : edge.type === 'breakdown' ? 'Breakdown Dissolved'
        : edge.type === 'contains' ? 'Contains Dissolved'
        : edge.type === 'elaboration' ? 'Elaboration Dissolved'
        : 'Relationship Dissolved'
      push({
        id: `edge-dissolved-${edge.id}`,
        type: 'relationship_dissolved' as const,
        timestamp: ts,
        label: dissolvedLabel,
        detail: other?.title ?? otherId?.slice(0, 8),
        isEstimated: true,
      })
    }
  })

  // Dedupe and sort
  const seen = new Set<string>()
  return events
    .filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return true })
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  itemId: string
  onEventCount?: (count: number) => void
}

export function ItemLineageTimeline({ itemId, onEventCount }: Props) {
  const item = useItemsStore((s: any) => s.items.find((i: any) => i.id === itemId))
  const allItems = useItemsStore((s: any) => s.items)
  const fetchItems = useItemsStore((s: any) => s.fetchFromSupabase)
  const { sessions } = useSessionStore()
  const itemPools = useItemPoolsStore((st) => st.itemPools)
  const { cycles } = useDegapProcessStore()
  const allGroups = useItemRelationshipsStore((s) => s.groups)
  const allGroupMemberships = useItemRelationshipsStore((s) => s.groupMemberships)
  const allEdges = useItemRelationshipsStore((s) => s.edges)
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase)
  const allParticipations = useCycleParticipationStore((s) => s.participations)
  const fetchParticipations = useCycleParticipationStore((s) => s.fetchFromSupabase)
  const fetchItemAuditTrail = useAuditStore((s) => s.fetchItemAuditTrail)
  const fetchRescoringEvents = useRescoringStore((s) => s.fetchForItem)
  const getRescoringEvents = useRescoringStore((s) => s.getEventsForItem)

  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([])
  const [lineageRows, setLineageRows] = useState<LineageEventRow[]>([])
  const [dataReady, setDataReady] = useState(false)

  useEffect(() => {
    if (!itemId) return
    setDataReady(false)
    const tasks: Promise<unknown>[] = [
      fetchItemAuditTrail(itemId).then(setAuditEntries).catch(() => {}),
      getLineageForItem(itemId).then(setLineageRows).catch(() => {}),
      fetchRelationships(),
      fetchRescoringEvents(itemId).catch(() => {}),
    ]
    if (allItems.length === 0) tasks.push(fetchItems())
    if (Object.keys(allParticipations).length === 0) tasks.push(fetchParticipations())
    Promise.allSettled(tasks).then(() => setDataReady(true))
    // Load once per item — avoid re-entering loading when store slices update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId])

  // A save writes new audit rows, so re-read them when History does — quietly,
  // without going back to "Loading timeline…".
  const saveTick = useItemSaveTick(itemId)
  useEffect(() => {
    if (saveTick === 0) return
    let cancelled = false
    fetchItemAuditTrail(itemId).then((entries) => { if (!cancelled) setAuditEntries(entries) }).catch(() => {})
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTick])

  const groups = useMemo(() => {
    const memberGroupIds = new Set(
      allGroupMemberships.filter((m) => m.itemId === itemId).map((m) => m.groupId),
    )
    return allGroups.filter((g) => memberGroupIds.has(g.id))
  }, [allGroups, allGroupMemberships, itemId])

  const cycleParticipations = useMemo(
    () => Object.values(allParticipations).filter((p) => p.itemId === itemId),
    [allParticipations, itemId],
  )

  const edges = useMemo(
    () => allEdges.filter((e) => e.sourceItemId === itemId || e.targetItemId === itemId),
    [allEdges, itemId],
  )

  const rescoringEvents = getRescoringEvents(itemId)
  // The workspace's own dimension names (Settings → Attributes), so a rescored
  // dimension reads the way the workspace calls it — and one it defined itself
  // does not break the timeline (roadmap 1C V-10).
  const workspaceAttributes = useAttributesStore((s) => s.attributes)

  // People a change names (a new owner, a delegate), so it reads as a name.
  const personIds = useMemo(() => namedPeopleIds(auditEntries), [auditEntries])
  const namedPeople = useActorDisplayMap(personIds)

  const rawEvents = useMemo(() => {
    if (!item || !dataReady) return []
    const derived = deriveEvents(item, sessions, cycles, groups, edges, allItems, cycleParticipations, auditEntries, lineageRows, itemPools, namedPeople)

    // Merge rescoring events as 'score_revised' timeline entries
    const rescoringTimelineEvents: (typeof derived[number])[] = rescoringEvents.map((re) => {
      const attrDef = rescoringAttributeDef(
        re.attributeKey,
        workspaceAttributes.find((a) => a.key === re.attributeKey)?.label,
      )
      const triggerDef = TRIGGER_TYPE_DEFS[re.triggerType] ?? TRIGGER_TYPE_DEFS.other
      const delta = re.revisedScore - re.previousScore
      const deltaStr = delta > 0 ? `+${delta}` : String(delta)
      return {
        id: `rescore-${re.id}`,
        type: 'score_revised' as const,
        timestamp: new Date(re.createdAt),
        label: `${attrDef.label} Rescored`,
        sublabel: triggerDef.label,
        detail: `${re.previousScore} → ${re.revisedScore} (${deltaStr}) · ${re.triggerDescription}`,
        actor: re.createdBy,
        stream: 'history' as const,
      }
    })

    return [...derived, ...rescoringTimelineEvents].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
    )
  }, [item, dataReady, sessions, cycles, groups, edges, allItems, cycleParticipations, auditEntries, lineageRows, rescoringEvents, itemPools, workspaceAttributes, namedPeople])

  // Resolve UUID actor IDs to display names
  const actorIds = useMemo(() => rawEvents.map((e) => e.actor ?? null), [rawEvents])
  const actorDisplayMap = useActorDisplayMap(actorIds)

  const events = useMemo(
    () =>
      rawEvents.map((e) => {
        const resolved = e.actor ? actorDisplayMap[e.actor] : undefined;
        // Suppress the raw UUID when the hook echoes it back unresolved.
        const actorDisplay =
          resolved && resolved !== e.actor ? resolved : undefined;
        return { ...e, actorDisplay };
      }),
    [rawEvents, actorDisplayMap],
  )

  if (!item) return null

  const journeyAction = (
    <Link
      to={`/prioritization/items/${itemId}/journey`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-medium hover:bg-[var(--green-dim)] hover:border-[var(--green)] transition-colors"
    >
      <MapIcon className="h-3 w-3" />
      Full journey
    </Link>
  )

  return (
    <LineageTimeline
      events={events}
      title="Item Timeline"
      onEventCount={onEventCount}
      isLoading={!dataReady}
      headerAction={journeyAction}
    />
  )
}
